import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FileText, Plus, Clock, CheckCircle } from 'lucide-react';
import AdminNavbar from './AdminNavbar';
import CreatePost from './CreatePost'; // Import the new modal component

const AuthorDashboard = ({ token, setAuthToken }) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false); // Controls the modal

  const fetchPosts = () => {
    // 1. Check for the token in props, fallback to localStorage if refreshing
    const activeToken = token || localStorage.getItem('token');

    // 2. If absolutely no token exists, send them to login
    if (!activeToken) {
      window.location.href = '/admin/login';
      return;
    }

    setLoading(true);
    axios.get(`${process.env.REACT_APP_BASE_URL}/backend/api/user/posts`, {
      headers: {
        'Authorization': `Bearer ${activeToken}`,
        'Accept': 'application/json' // <-- THIS IS THE MAGIC FIX
      }
    })
      .then(res => {
        setPosts(res.data);
        setLoading(false);
      })
      .catch(err => {
        console.error(err);

        // 3. Catch the 401 error. If the session expired, clear storage and log out.
        if (err.response?.status === 401) {
          localStorage.removeItem('token');
          localStorage.removeItem('userName');
          localStorage.removeItem('userRole');
          window.location.href = '/admin/login';
        } else {
          setLoading(false);
        }
      });
  };

  useEffect(() => {
    fetchPosts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <div className="min-h-screen bg-gray-50 relative">
      <AdminNavbar setAuthToken={setAuthToken} />

      {/* Modal Overlay */}
      {isModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-brand-dark/80 backdrop-blur-sm p-4">
          <CreatePost
            token={token}
            onClose={() => setIsModalOpen(false)}
            onSuccess={fetchPosts}
          />
        </div>
      )}

      <div className="pt-32 pb-24 container mx-auto px-6 max-w-6xl">
        <div className="flex flex-col md:flex-row justify-between items-center mb-10 gap-4">
          <div>
            <h1 className="text-3xl font-heading font-black text-brand-dark">My Articles</h1>
            <p className="text-gray-500 mt-1">Manage your submitted blog posts.</p>
          </div>

          {/* Now a standard button instead of a Link */}
          <button
            onClick={() => setIsModalOpen(true)}
            className="px-6 py-3 bg-brand-cyan text-brand-dark font-bold rounded-xl shadow-md hover:shadow-lg transition-all flex items-center gap-2"
          >
            <Plus size={20} /> Draft New Article
          </button>
        </div>

        <div className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden">
          {loading ? (
            <div className="p-12 text-center text-gray-500">Loading your posts...</div>
          ) : posts.length === 0 ? (
            <div className="p-16 text-center">
              <FileText className="mx-auto text-gray-300 mb-4" size={48} />
              <h3 className="text-xl font-bold text-gray-700">No articles yet</h3>
              <p className="text-gray-500 mt-2">Start writing to see your history here.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-brand-dark text-white text-sm uppercase tracking-wider">
                    <th className="p-6 font-bold">Article Title</th>
                    <th className="p-6 font-bold">Category</th>
                    <th className="p-6 font-bold">Date Submitted</th>
                    <th className="p-6 font-bold">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {posts.map(post => (
                    <tr key={post.id} className="hover:bg-gray-50 transition-colors">
                      <td className="p-6 font-bold text-brand-dark">{post.title}</td>
                      <td className="p-6 text-gray-600">{post.category || 'N/A'}</td>
                      <td className="p-6 text-gray-500">
                        {new Date(post.created_at).toLocaleDateString()}
                      </td>
                      <td className="p-6">
                        {post.status === 'published' ? (
                          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-green-100 text-green-700 text-xs font-bold uppercase">
                            <CheckCircle size={14} /> Published
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-yellow-100 text-yellow-700 text-xs font-bold uppercase">
                            <Clock size={14} /> Pending Review
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthorDashboard;